import { CountrySelector } from '@/components/common/CountrySelector';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Shield } from 'lucide-react';
import { memo } from 'react';
import type { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import type { IdentificationFormData } from '../types';

interface ContractingPartySectionProps {
  control: Control<IdentificationFormData>;
  country: string;
  legalForm: string;
  legalForms: Array<{ value: string; label: string }>;
}

export const ContractingPartySection = memo(function ContractingPartySection({
  control,
  country,
  legalForm,
  legalForms,
}: ContractingPartySectionProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-4'>
        <Shield className='w-5 h-5' />
        <h3 className='text-lg font-semibold'>
          {tForms('identification.sections.contractingParty')}
        </h3>
      </div>

      {/* Row 1: Company Name & Address */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='companyName'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.companyName')}
              name='companyName'
              required
              tooltip={tForms('identification.tooltips.companyName')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('identification.placeholders.companyName')}
                className='min-h-[44px]'
                autoComplete='organization'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='headOfficeAddress'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.headOfficeAddress')}
              name='headOfficeAddress'
              required
              tooltip={tForms('identification.tooltips.headOfficeAddress')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.headOfficeAddress'
                )}
                className='min-h-[44px]'
                autoComplete='street-address'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 2: Postcode & City */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='postcode'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.postcode')}
              name='postcode'
              required
              tooltip={tForms('identification.tooltips.postcode')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('identification.placeholders.postcode')}
                className='min-h-[44px]'
                autoComplete='postal-code'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='city'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.city')}
              name='city'
              required
              tooltip={tForms('identification.tooltips.city')}
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('identification.placeholders.city')}
                className='min-h-[44px]'
                autoComplete='address-level2'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      {/* Row 3: Country & Legal Form */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='country'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.country')}
              name='country'
              required
              tooltip={tForms('identification.tooltips.country')}
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms('identification.placeholders.country')}
                error={fieldState.error?.message}
                required
                name='country'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name='legalForm'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.legalForm')}
              name='legalForm'
              required
              tooltip={tForms('identification.tooltips.legalForm')}
              error={fieldState.error?.message}
            >
              {country === 'CH' ? (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className='min-h-[44px] w-full'>
                    <SelectValue
                      placeholder={tForms(
                        'identification.placeholders.legalForm'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {legalForms.map(form => (
                      <SelectItem key={form.value} value={form.value}>
                        {form.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  {...field}
                  placeholder={tForms('identification.placeholders.legalForm')}
                  className='min-h-[44px]'
                />
              )}
            </FormFieldWrapper>
          )}
        />
      </div>

      {country === 'CH' && legalForm === 'other' && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
          <FormField
            control={control}
            name='legalFormOther'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('identification.fields.legalFormOther')}
                name='legalFormOther'
                required
                tooltip={tForms('identification.tooltips.legalFormOther')}
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  placeholder={tForms(
                    'identification.placeholders.legalFormOther'
                  )}
                  className='min-h-[44px]'
                />
              </FormFieldWrapper>
            )}
          />
        </div>
      )}
    </div>
  );
});
